// ErrorBoundary — per-panel React error boundary (PSD §10.8).
//
// Catches render-time exceptions thrown by its subtree and shows a minimal
// fallback instead of unmounting the whole app. The fallback's primary action
// is "Report this error" when an `onReportError` callback is available (wired
// via FeedbackContext in App.tsx → handleOpenFeedback('bug_report','bug',…)),
// otherwise it offers "Reload page".

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { useFeedbackOpener } from './feedbackContext';

interface ErrorBoundaryProps {
  children: ReactNode;
  boundaryName?: string | undefined;
  onReportError?: ((err: Error) => void) | undefined;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(`[ErrorBoundary${this.props.boundaryName ? `: ${this.props.boundaryName}` : ''}]`, error, info);
  }

  // Reset on prop change so navigating away from a broken panel recovers
  // without a full reload.
  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    if (this.state.hasError && prevProps.children !== this.props.children) {
      this.setState({ hasError: false, error: null });
    }
  }

  render(): ReactNode {
    if (!this.state.hasError || this.state.error === null) {
      return this.props.children;
    }
    const err = this.state.error;
    const heading = this.props.boundaryName ?? 'Panel';
    const report = this.props.onReportError;
    return (
      <div className="error-state my-4" role="alert">
        <div className="error-state-icon" aria-hidden="true">
          <X size={28} strokeWidth={1.5} />
        </div>
        <p>
          <strong>Something went wrong</strong> ({heading})
        </p>
        <p style={{ fontSize: 'var(--font-size-1)' }}>
          {err.name}: {err.message}
        </p>
        <div className="mt-3">
          {report ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => report(err)}
            >
              Report this error
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => window.location.reload()}
            >
              Reload page
            </button>
          )}
        </div>
      </div>
    );
  }
}

/**
 * Wrapper that pulls the optional feedback opener from FeedbackContext and
 * forwards it as `onReportError`. Use this inside the provider tree so each
 * boundary's "Report this error" button reaches `handleOpenFeedback`.
 */
export function PanelErrorBoundary({
  children,
  boundaryName,
}: {
  children: ReactNode;
  boundaryName?: string | undefined;
}): ReactNode {
  const openFeedback = useFeedbackOpener();
  const report = openFeedback
    ? (err: Error) => openFeedback('bug_report', 'bug', err.message)
    : undefined;
  return (
    <ErrorBoundary boundaryName={boundaryName} onReportError={report}>
      {children}
    </ErrorBoundary>
  );
}
